import {timingFunctions} from 'polished';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import noColor from '../../../assets/images/noColor.svg';
import noColorSelected from '../../../assets/images/noColorSelected.svg';

/*
 * Props.
 */

interface ColorPickerItemProps {
  color: string;
  hoverColor: string;
  shouldAddBorder?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
}

/*
 * Style.
 */

const StyledWrapperButton = styled(ChromelessButton)`
  position: relative;
  width: 24px;
  height: 24px;
`;

interface ColorPickerItemStyleProps {
  $color: string;
  $hoverColor: string;
  $shouldAddBorder?: boolean;
  $isSelected?: boolean;
  $onClick?: () => void;
}

const StyledBaseDiv = styled.div<ColorPickerItemStyleProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 6px;
`;

const StyledColorDiv = styled(StyledBaseDiv)`
  background-color: ${(p) => p.$color || p.theme.foreground};
  ${(p) => maybeAddBorderStyle(p)};
`;

function maybeAddBorderStyle(props: ColorPickerItemStyleProps) {
  if (!props.$shouldAddBorder) {
    return '';
  }

  return css`
    box-shadow: inset 0 0 0 1px ${(p) => p.theme.greys.shade50};
  `;
}

const StyledHoverDiv = styled(StyledBaseDiv)`
  background-color: ${(p) => p.$hoverColor};
  transition: transform 150ms ${timingFunctions('easeInBack')};
  ${(p) => maybeAddHoverStyle(p)};
`;

function maybeAddHoverStyle(props: ColorPickerItemStyleProps) {
  if (props.$isSelected) {
    return '';
  }

  return css`
    div:hover > && {
      transform: scale(1.4);
      transition-timing-function: ${timingFunctions('easeOutBack')};
    }
  `;
}

const StyledImageDiv = styled.div`
  ${center()};
`;

/*
 * Component.
 */

export const ColorPickerItem: FC<ColorPickerItemProps> = (props) => (
  <StyledWrapperButton onClick={props.onClick} shouldPreventMouseDown>
    <StyledHoverDiv
      $color={props.color}
      $hoverColor={props.hoverColor}
      $shouldAddBorder={props.shouldAddBorder}
      $isSelected={props.isSelected}
      $onClick={props.onClick}
    />
    <StyledColorDiv
      $color={props.color}
      $hoverColor={props.hoverColor}
      $shouldAddBorder={props.shouldAddBorder}
      $isSelected={props.isSelected}
      $onClick={props.onClick}
    />
    {maybeRenderNoColor(props)}
    {maybeRenderCheckmark(props)}
  </StyledWrapperButton>
);

function maybeRenderNoColor(props: ColorPickerItemProps) {
  if (props.color !== '') {
    return null;
  }

  const image = props.isSelected ? noColorSelected : noColor;
  return (
    <StyledImageDiv>
      <img alt="no-color" src={image} />
    </StyledImageDiv>
  );
}

function maybeRenderCheckmark(props: ColorPickerItemProps) {
  if (!props.isSelected) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => {
        const color = props.shouldAddBorder ? theme.palette.blue.shade40 : theme.greys.white;
        return (
          <StyledImageDiv>
            <Icon name="checkmarkSelectedSmall" color={color} />
          </StyledImageDiv>
        );
      }}
    </UserThemeConsumer>
  );
}
